import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

export const FOOTER_GROUPS = [
  { title: "Product", links: [["/#features", "Features"], ["/pricing", "Pricing"], ["/vs/distrokid-tunecore-cdbaby", "Compare distributors"], ["/convert", "Audio & image converter"], ["/#faq", "FAQ"]] },
  { title: "Guides", links: [["/ai-music-distribution", "AI music distribution"], ["/distribute-suno-music", "Distribute Suno music"], ["/suno-to-spotify", "Suno to Spotify"], ["/distribute-udio-music", "Distribute Udio music"], ["/ai-music-directory", "AI music tools directory"], ["/blog", "Blog"]] },
  { title: "For business", links: [["/music-distribution-api", "Distribution API"], ["/docs/api", "API docs"], ["/white-label-music-distribution", "White-label"]] },
  { title: "Company", links: [["/about", "About us"], ["/contact", "Contact"], ["/privacy", "Privacy"], ["/terms", "Terms"], ["/legal/copyright", "Copyright dispute"], ["/legal/copyright#dmca-policy", "DMCA policy"], ["/legal/copyright#copyright-policy", "Copyright policy"], ["/legal/copyright#report-abuse", "Report abuse"]] },
];

// Theme-aware class helper for marketing pages (dark default, light friendly).
export function useMk() {
  const { theme } = useTheme();
  const L = theme === "light";
  return {
    L,
    page: L ? "bg-[#FAFAF7] text-zinc-900" : "bg-[#09090B] text-white",
    card: L ? "bg-white border-zinc-200 shadow-sm" : "bg-[#0F0F11] border-zinc-800",
    muted: L ? "text-zinc-600" : "text-zinc-400",
    soft: L ? "bg-zinc-100" : "bg-zinc-900/60",
    line: L ? "border-zinc-200" : "border-zinc-800",
  };
}

// Header CTA: logged-in users jump to their dashboard, guests create an account.
export function NavCta({ guest = "Start free", className = "", testid, children }) {
  const { user } = useAuth();
  const to = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/auth?signup=1";
  return <Link to={to} className={className} data-testid={testid}>{user ? <><span className="sm:hidden">Dashboard</span><span className="hidden sm:inline">Launch Dashboard</span></> : guest}{children}</Link>;
}

// Same top menu as the landing page — the new SEO pages live in the footer only.
const NAV = [["/#features", "Features"], ["/#pricing", "Pricing"], ["/#faq", "FAQ"], ["/blog", "Blog"], ["/contact", "Contact"]];

// Shared top menu used by every public page.
export function MarketingHeader({ fixed = false }) {
  const m = useMk();
  const [open, setOpen] = useState(false);
  return (
      <header className={`${fixed ? "fixed top-0 left-0 right-0 z-50" : "sticky top-0 z-40"} backdrop-blur-xl border-b ${m.line} ${m.L ? "bg-white/80" : "bg-[#09090B]/80"}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="min-w-0 shrink" data-testid="mk-logo"><BrandLogo /></Link>
          <nav className={`hidden md:flex items-center gap-6 text-sm ${m.muted}`}>
            {NAV.map(([to, l]) => <Link key={to} to={to} className="hover:text-[#FF4500] transition-colors" data-testid={`mk-nav-${l.toLowerCase()}`}>{l}</Link>)}
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <button type="button" onClick={() => setOpen((o) => !o)} className="md:hidden p-2" aria-label="Menu" data-testid="mk-mobile-menu-btn">{open ? <X size={20} /> : <Menu size={20} />}</button>
            <ThemeToggle />
            <NavCta className="shrink-0 whitespace-nowrap px-3 sm:px-4 py-2 rounded-full bg-[#FF4500] text-black text-xs sm:text-sm font-semibold hover:bg-[#FF6A33] transition-colors" testid="mk-nav-cta" />
          </div>
        </div>
        {open && (
          <nav className={`md:hidden border-t ${m.line} px-5 py-3 flex flex-col gap-3 text-sm`} data-testid="mk-mobile-menu">
            {NAV.map(([to, l]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="py-1 hover:text-[#FF4500]" data-testid={`mk-mobile-nav-${l.toLowerCase()}`}>{l}</Link>)}
          </nav>
        )}
      </header>
  );
}

export function MarketingShell({ children, testid }) {
  const m = useMk();
  return (
    <div className={`min-h-screen ${m.page}`} data-testid={testid}>
      <MarketingHeader />
      <main>{children}</main>
      <SeoFooter />
    </div>
  );
}

export function SeoFooter() {
  const m = useMk();
  return (
    <footer className={`border-t ${m.line} mt-24`} data-testid="seo-footer">
      <div className="max-w-6xl mx-auto px-5 py-12 grid gap-10 lg:grid-cols-[1.3fr_3fr]">
        <div>
          <BrandLogo />
          <p className={`text-sm mt-3 max-w-xs leading-relaxed ${m.muted}`}>The AI-first music distributor. Release Suno, Udio and human-made music to Spotify, Apple Music, TikTok and 150+ stores — keep up to 100% of royalties.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
          {FOOTER_GROUPS.map((g) => (
            <nav key={g.title} aria-label={g.title}>
              <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#FF4500] mb-3">{g.title}</div>
              <ul className="space-y-2.5 text-sm">
                {g.links.map(([to, label]) => (
                  <li key={to}><Link to={to} className={`${m.muted} hover:text-[#FF4500] transition-colors`} data-testid={`seo-footer-${to.replace(/\W+/g, "-")}`}>{label}</Link></li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
      </div>
      <div className={`border-t ${m.line}`}>
        <div className={`max-w-6xl mx-auto px-5 py-5 text-xs flex flex-col sm:flex-row gap-2 justify-between ${m.muted}`}>
          <span>© {new Date().getFullYear()} DistributeMusic.AI. All rights reserved.</span>
          <span>Exclusively made for AI music.</span>
        </div>
      </div>
    </footer>
  );
}
